import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoredLanguage, normalizeLanguage, setStoredLanguage, type SupportedLanguage } from './language';

/**
 * The app language for a picker: the stored choice (localStorage `kiosk_language`), kept in step
 * with i18next. Shared by the header LanguageSwitcher (signed out) and Me → Language.
 */
export function useAppLanguage(): { language: SupportedLanguage; setLanguage: (next: SupportedLanguage) => void } {
  const { i18n } = useTranslation();
  const [language, setLang] = useState<SupportedLanguage>(() => getStoredLanguage());

  // On mount: the stored choice wins over whatever i18next started with.
  useEffect(() => {
    const next = getStoredLanguage();
    setLang(next);
    if (normalizeLanguage(i18n.language) !== next) {
      void i18n.changeLanguage(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Another picker (or code) changed the language: follow it.
  useEffect(() => {
    const onLangChanged = (lng: string) => {
      setLang(normalizeLanguage(lng));
    };
    i18n.on('languageChanged', onLangChanged);
    return () => {
      i18n.off('languageChanged', onLangChanged);
    };
  }, [i18n]);

  const setLanguage = useCallback(
    (next: SupportedLanguage) => {
      setStoredLanguage(next);
      setLang(next);
      void i18n.changeLanguage(next);
    },
    [i18n],
  );

  return { language, setLanguage };
}

export default useAppLanguage;
